import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { dropdownVariants, cardHover } from '../../utils/animations';
import { SkeletonStatCard, SkeletonReviewCard, Drawer } from '../ui';
import { useWebsite } from '../../contexts/WebsiteContext';
import { Eyebrow, HairlineGrid, Stat, Tag, type TagTone } from '../ui/kit';
import EmptyState from '../ui/EmptyState';
import PlanUpsell from '../ui/PlanUpsell';
import { MARKS } from '../../lib/marks';
import { usePlanFeatures } from '../../hooks/usePlanFeatures';

// Hook to track previous value (for comparing what items existed before)
function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

// Icons - defined first so they can be used in config objects
const StarIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
  </svg>
);

const PlusIcon: React.FC = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const ChatIcon: React.FC = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const ClockIcon: React.FC = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const GoogleIcon: React.FC = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const YelpIcon: React.FC = () => (
  <svg className="w-5 h-5" viewBox="-6 0 48 48" fill="#D32200">
    <path d="M.917 23.108c-.767 1.25-1.09 5.184-.826 7.796.094.862.248 1.58.47 2.01.307.592.823.946 1.412.966.378.02.612-.046 7.704-2.363l3.151-1.02c.785-.2 1.314-.936 1.367-1.875.05-.96-.44-1.81-1.245-2.116l-2.226-.92C3.117 22.38 2.776 22.26 2.39 22.25c-.587-.02-1.113.28-1.474.858zm17.048 23.795c.124-.36.138-.612.159-8.182l.018-3.343c.05-.817-.43-1.563-1.288-1.9-.88-.344-1.827-.13-2.358.544l-1.554 1.875c-5.342 6.37-5.56 6.665-5.692 7.043-.079.225-.112.46-.083.695.03.336.178.672.429.98 1.237 1.497 7.17 3.717 9.065 3.389.66-.124 1.137-.5 1.343-1.064zm12.041-2.634c1.79-.725 5.696-5.772 5.971-7.714.094-.675-.113-1.26-.572-1.63-.299-.23-.532-.32-7.618-2.682l-3.108-1.044c-.751-.296-1.612-.023-2.19.704-.598.743-.691 1.72-.213 2.463l1.252 2.068c4.209 6.944 4.53 7.434 4.833 7.674.469.375 1.064.436 1.685.191zm-3.66-17.872c8.054-1.98 8.365-2.088 8.684-2.3.486-.33.73-.89.69-1.57 0-.024.003-.044 0-.07-.207-2.003-3.674-7.238-5.38-8.086-.604-.295-1.212-.274-1.71.066-.312.207-.543.517-4.855 6.508l-1.95 2.698c-.515.633-.52 1.545-.02 2.325.52.807 1.398 1.2 2.203.974l-.042.07c.397-.155 1.108-.33 2.4-.642zm-8.088-6.757c-.137-3.242-1.096-17.684-1.209-18.354-.166-.604-.63-1.038-1.302-1.21-2.054-.52-9.912 1.72-11.368 3.247-.468.495-.643 1.107-.502 1.646.228.48 9.972 16.044 9.972 16.044 1.441 2.372 2.614 2.004 2.998 1.882.383-.12 1.55-.49 1.417-3.255z" />
  </svg>
);

const FacebookIcon: React.FC = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const TrustpilotIcon: React.FC = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
);

/** A closed envelope — private feedback from the site's /review page. */
const PrivateFeedbackIcon: React.FC = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m2 7 10 6 10-6" />
  </svg>
);

interface Review {
  id: string;
  websiteId: string;
  platform: string;
  rating: number;
  reviewerName?: string | null;
  reviewText?: string | null;
  reviewUrl?: string | null;
  responded: boolean;
  response?: string | null;
  respondedAt?: string | null;
  reviewDate: string;
  createdAt: string;
}

interface ReviewRequest {
  id: string;
  websiteId: string;
  customerName: string;
  customerEmail?: string | null;
  customerPhone?: string | null;
  status: string;
  sentAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
}

interface Website {
  id: string;
  domain: string;
  businessName?: string;
}

const platformConfig: Record<string, { label: string; tone: TagTone; tileClass: string; icon: React.ReactNode }> = {
  google: { label: 'Google', tone: 'info', tileClass: 'bg-sky-50 text-sky-800 ring-sky-600/20', icon: <GoogleIcon /> },
  // Private feedback from the site's own /review page — never public, never on
  // Google. Labelled distinctly so it is not mistaken for a published review.
  'site-feedback': { label: 'Private feedback', tone: 'warn', tileClass: 'bg-amber-50 text-amber-800 ring-amber-600/20', icon: <PrivateFeedbackIcon /> },
  yelp: { label: 'Yelp', tone: 'danger', tileClass: 'bg-red-50 text-red-800 ring-red-600/20', icon: <YelpIcon /> },
  facebook: { label: 'Facebook', tone: 'violet', tileClass: 'bg-violet-50 text-violet-800 ring-violet-600/20', icon: <FacebookIcon /> },
  trustpilot: { label: 'Trustpilot', tone: 'live', tileClass: 'bg-emerald-50 text-emerald-800 ring-emerald-600/20', icon: <TrustpilotIcon /> },
};

const requestStatusConfig: Record<string, { label: string; tone: TagTone }> = {
  pending: { label: 'Pending', tone: 'neutral' },
  sent: { label: 'Sent', tone: 'info' },
  completed: { label: 'Completed', tone: 'live' },
  declined: { label: 'Declined', tone: 'danger' },
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Skeleton loading component
const ReviewsPageSkeleton: React.FC = () => (
  <div className="space-y-6">
    {/* Header Skeleton */}
    <div>
      <div className="h-8 bg-zinc-200 rounded-lg w-48 animate-pulse" />
      <div className="h-4 bg-zinc-200 rounded w-72 mt-2 animate-pulse" />
    </div>

    {/* Stats Skeleton */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <SkeletonStatCard key={i} />
      ))}
    </div>

    {/* Tabs Skeleton */}
    <div className="flex gap-6 border-b border-zinc-200 pb-3">
      <div className="h-6 bg-zinc-200 rounded w-24 animate-pulse" />
      <div className="h-6 bg-zinc-200 rounded w-32 animate-pulse" />
    </div>

    {/* Reviews Skeleton */}
    <div className="space-y-4">
      {[...Array(3)].map((_, i) => (
        <SkeletonReviewCard key={i} />
      ))}
    </div>
  </div>
);

const ReviewsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { selectedWebsiteId, isAllSelected, websites } = useWebsite();
  const selectedWebsite = isAllSelected ? '' : selectedWebsiteId;
  const currentWebsite = websites.find(w => w.id === selectedWebsite) || websites[0];

  const [activeTab, setActiveTab] = useState<'reviews' | 'requests'>('reviews');
  const { plan } = usePlanFeatures();
  const [filterPlatform, setFilterPlatform] = useState<string>('');
  const [filterRating, setFilterRating] = useState<string>('');
  const [filterResponded, setFilterResponded] = useState<string>('');
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [respondingTo, setRespondingTo] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const websitesLoading = false; // websites loaded by context

  // Fetch reviews
  const { data: reviewsData, isLoading: reviewsLoading } = useQuery({
    queryKey: ['reviews', selectedWebsite, filterPlatform, filterRating, filterResponded],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterPlatform) params.append('platform', filterPlatform);
      if (filterRating) params.append('rating', filterRating);
      if (filterResponded) params.append('responded', filterResponded);
      const response = await axios.get(`/api/reviews?${params.toString()}`);
      return response.data.data;
    },
    // Keep previous data visible while fetching new data (prevents remount)
    placeholderData: (previousData) => previousData,
  });

  // Fetch review requests
  const { data: requestsData, isLoading: requestsLoading } = useQuery({
    queryKey: ['reviewRequests', selectedWebsite],
    queryFn: async () => {
      const response = await axios.get('/api/reviews/requests');
      return response.data.data;
    },
    // Keep previous data visible while fetching new data (prevents remount)
    placeholderData: (previousData) => previousData,
  });

  // Update review (add response) mutation
  const updateReviewMutation = useMutation({
    mutationFn: async ({ id, response }: { id: string; response: string }) => {
      const res = await axios.patch(`/api/reviews/${id}`, { response });
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      setRespondingTo(null);
    },
  });

  const reviews = reviewsData?.reviews || [];
  const requests = requestsData?.requests || [];

  // Track which IDs existed in the previous render
  // This lets us animate only truly NEW items, not items that were just filtered back in
  const reviewIds = reviews.map((r: Review) => r.id);
  const previousReviewIds = usePrevious(reviewIds);
  const previousReviewIdSet = new Set(previousReviewIds || []);

  const requestIds = requests.map((r: ReviewRequest) => r.id);
  const previousRequestIds = usePrevious(requestIds);
  const previousRequestIdSet = new Set(previousRequestIds || []);

  // Calculate stats
  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum: number, r: Review) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : '0.0';
  const respondedCount = reviews.filter((r: Review) => r.responded).length;
  const pendingCount = reviews.filter((r: Review) => !r.responded).length;

  // Only show skeleton on true initial load (no data at all)
  // During filter changes, placeholderData keeps previous data visible
  const showSkeleton = websitesLoading || (reviewsLoading && !reviewsData) || (requestsLoading && !requestsData);

  if (showSkeleton) {
    return <ReviewsPageSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Eyebrow className="mb-3">Performance</Eyebrow>
          <h1 className="font-display text-[2rem] font-bold leading-[1.1] tracking-[-0.03em] text-primary">
            Reviews {!isAllSelected && currentWebsite && websites.length > 1 ? `for ${currentWebsite.businessName || currentWebsite.domain}` : ''}
          </h1>
          <p className="text-muted mt-1">
            {websites.length === 1
              ? `Manage customer reviews for ${currentWebsite?.businessName || currentWebsite?.domain}`
              : 'Manage customer reviews and request new ones'
            }
          </p>
        </div>
        <div className="flex items-center gap-2">
          <motion.button
            onClick={async () => {
              if (!selectedWebsite) return;
              setSyncing(true);
              try {
                const res = await axios.post('/api/reviews/sync-google', { websiteId: selectedWebsite });
                const { created, skipped } = res.data?.data || {};
                if (created > 0) {
                  queryClient.invalidateQueries({ queryKey: ['reviews'] });
                  queryClient.invalidateQueries({ queryKey: ['review-stats'] });
                }
                console.log(`Sync complete: ${created} new, ${skipped} skipped`);
              } catch (err: any) {
                const msg = err.response?.data?.error || 'Sync failed';
                console.error('Review sync error:', msg);
              } finally {
                setSyncing(false);
              }
            }}
            disabled={syncing || !selectedWebsite}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 text-sm font-medium text-primary ring-1 ring-zinc-300 transition-all hover:bg-white hover:ring-zinc-400 disabled:opacity-50"
          >
            <svg className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
            {syncing ? 'Syncing...' : 'Sync from Google'}
          </motion.button>
          {(reviews.length > 0 || requests.length > 0) && (
            <motion.button
              onClick={() => setShowRequestModal(true)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 text-sm font-medium text-primary ring-1 ring-zinc-300 transition-all hover:bg-white hover:ring-zinc-400"
            >
              <PlusIcon />
              Request Review
            </motion.button>
          )}
        </div>
      </div>

      {/* Stats Overview — hairline cells, no icon tiles */}
      <HairlineGrid cols="grid-cols-2 lg:grid-cols-4">
        <Stat label="Average rating" value={avgRating} />
        <Stat label="Total reviews" value={reviews.length} />
        <Stat label="Responded" value={respondedCount} />
        <Stat label="Pending response" value={pendingCount} />
      </HairlineGrid>

      {/* Tabs */}
      <div className="border-b border-zinc-200">
        <div className="flex gap-6 relative">
          <button
            onClick={() => setActiveTab('reviews')}
            className={`pb-3 text-sm font-medium transition-colors border-b-2 -mb-px relative ${
              activeTab === 'reviews'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted hover:text-primary'
            }`}
          >
            Reviews ({reviews.length})
            {activeTab === 'reviews' && (
              <motion.div
                layoutId="activeTab"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                transition={{ type: 'spring', stiffness: 500, damping: 35 }}
              />
            )}
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`pb-3 text-sm font-medium transition-colors border-b-2 -mb-px relative ${
              activeTab === 'requests'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted hover:text-primary'
            }`}
          >
            Review Requests ({requests.length})
            {activeTab === 'requests' && (
              <motion.div
                layoutId="activeTab"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                transition={{ type: 'spring', stiffness: 500, damping: 35 }}
              />
            )}
          </button>
        </div>
      </div>

      {/* Reviews Tab */}
      <AnimatePresence mode="wait">
        {activeTab === 'reviews' && (
          <motion.div
            key="reviews"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {/* Filters - Google Style */}
            <div className="flex flex-wrap gap-2 mb-6">
              <FilterDropdown
                value={filterPlatform}
                onChange={setFilterPlatform}
                options={[
                  { value: '', label: 'All Platforms' },
                  { value: 'google', label: 'Google' },
                  { value: 'yelp', label: 'Yelp' },
                  { value: 'facebook', label: 'Facebook' },
                  { value: 'trustpilot', label: 'Trustpilot' },
                  { value: 'site-feedback', label: 'Private feedback' },
                ]}
              />
              <FilterDropdown
                value={filterRating}
                onChange={setFilterRating}
                options={[
                  { value: '', label: 'All Ratings' },
                  { value: '5', label: '5 Stars' },
                  { value: '4', label: '4 Stars' },
                  { value: '3', label: '3 Stars' },
                  { value: '2', label: '2 Stars' },
                  { value: '1', label: '1 Star' },
                ]}
              />
              <FilterDropdown
                value={filterResponded}
                onChange={setFilterResponded}
                options={[
                  { value: '', label: 'All Responses' },
                  { value: 'false', label: 'Needs Response' },
                  { value: 'true', label: 'Responded' },
                ]}
              />
              <AnimatePresence>
                {(filterPlatform || filterRating || filterResponded) && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    onClick={() => {
                      setFilterPlatform('');
                      setFilterRating('');
                      setFilterResponded('');
                    }}
                    className="px-3 py-2 text-sm text-muted hover:text-primary transition-colors"
                  >
                    Clear filters
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            {/* Reviews List */}
            {reviews.length === 0 ? (
              <EmptyState
                mark={MARKS.reviews}
                eyebrow="Reputation"
                title="No reviews yet"
                description="Reviews you've earned on Google, Yelp and Facebook are collected here so you can reply in one place."
                action={{ label: 'Request Review', onClick: () => setShowRequestModal(true) }}
                hint="New reviews are pulled in automatically once your Google profile is connected."
              />
            ) : (
              <div className="space-y-4">
                {reviews.map((review: Review, index: number) => {
                  // Only animate items that are truly new (didn't exist in previous render)
                  const isExistingItem = previousReviewIdSet.has(review.id);
                  return (
                    <ReviewCard
                      key={review.id}
                      review={review}
                      index={index}
                      isResponding={respondingTo === review.id}
                      onStartRespond={() => setRespondingTo(review.id)}
                      onCancelRespond={() => setRespondingTo(null)}
                      onSubmitResponse={(response) => updateReviewMutation.mutate({ id: review.id, response })}
                      isSubmitting={updateReviewMutation.isPending}
                      skipAnimation={isExistingItem}
                    />
                  );
                })}
              </div>
            )}
          </motion.div>
        )}

        {/* Requests Tab */}
        {activeTab === 'requests' && (
          <motion.div
            key="requests"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="space-y-6"
          >
            {/* Sending each request by hand is the manual version of a Growth
                feature. Promo only — nothing on this tab is gated. */}
            {plan !== 'growth' && (
              <PlanUpsell
                mark={MARKS.reviewEngine}
                requiredPlan="growth"
                title="Stop asking for reviews one at a time"
                description="The review funnel texts and emails every finished customer for you, and routes their feedback to us before it goes public."
                includes={[
                  'SMS and email sent automatically after each job',
                  'Feedback routed to us before it goes public',
                  'Happy customers pointed straight to Google',
                ]}
              />
            )}
            {requests.length === 0 ? (
              // Unlike the gated screens, sending a request by hand still works
              // here — so the empty state keeps its action and only gives up its
              // drawing, to avoid stacking two illustrations under the promo.
              <EmptyState
                mark={plan === 'growth' ? MARKS.notifications : undefined}
                compact={plan !== 'growth'}
                eyebrow={plan === 'growth' ? 'Requests' : undefined}
                title="No review requests"
                description="Ask a finished customer for a review and it shows up here until they leave one."
                action={{ label: 'Request Review', onClick: () => setShowRequestModal(true) }}
                hint={plan === 'growth' ? 'Requests go out by email or text, whichever you have on file.' : undefined}
              />
            ) : (
              <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                <div className="divide-y divide-zinc-100">
                  {requests.map((request: ReviewRequest, index: number) => {
                    // Only animate items that are truly new (didn't exist in previous render)
                    const isExistingItem = previousRequestIdSet.has(request.id);
                    return (
                      <RequestRow key={request.id} request={request} index={index} skipAnimation={isExistingItem} />
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Request Review Drawer */}
      {currentWebsite && (
        <RequestReviewDrawer
          isOpen={showRequestModal}
          onClose={() => setShowRequestModal(false)}
          websiteId={currentWebsite.id}
          websiteName={currentWebsite.businessName || currentWebsite.domain}
        />
      )}
    </div>
  );
};

// Review Card Component
interface ReviewCardProps {
  review: Review;
  index: number;
  isResponding: boolean;
  onStartRespond: () => void;
  onCancelRespond: () => void;
  onSubmitResponse: (response: string) => void;
  isSubmitting: boolean;
  skipAnimation?: boolean;
}

const ReviewCard: React.FC<ReviewCardProps> = ({
  review,
  index,
  isResponding,
  onStartRespond,
  onCancelRespond,
  onSubmitResponse,
  isSubmitting,
  skipAnimation,
}) => {
  const [responseText, setResponseText] = useState('');
  const platform = platformConfig[review.platform] || platformConfig.google;

  const handleSubmit = () => {
    if (responseText.trim()) {
      onSubmitResponse(responseText);
      setResponseText('');
    }
  };

  return (
    <motion.div
      initial={skipAnimation ? false : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={skipAnimation ? { duration: 0 } : { delay: index * 0.05, duration: 0.3 }}
      whileHover={{ y: -2 }}
      className="bg-white border border-zinc-200 rounded-xl p-6 transition-shadow hover:shadow-md"
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-lg ring-1 ${platform.tileClass}`}>
            {platform.icon}
          </div>
          <div>
            <h3 className="font-medium text-primary">{review.reviewerName || 'Anonymous'}</h3>
            <div className="flex items-center gap-2 mt-0.5">
              <Tag tone={platform.tone}>{platform.label}</Tag>
              <span className="text-xs text-muted">{formatDate(review.reviewDate)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <StarIcon
              key={star}
              className={`w-5 h-5 ${star <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-zinc-200'}`}
            />
          ))}
        </div>
      </div>

      {/* Review Text */}
      {review.reviewText && (
        <p className="mt-4 text-primary whitespace-pre-line">{review.reviewText}</p>
      )}

      {/* Response */}
      {review.responded && review.response && (
        <div className="mt-4 bg-zinc-50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
            >
              <Tag tone="live">Responded</Tag>
            </motion.div>
            {review.respondedAt && (
              <span className="text-xs text-muted">{formatDate(review.respondedAt)}</span>
            )}
          </div>
          <p className="text-sm text-muted">{review.response}</p>
        </div>
      )}

      {/* Response Form */}
      <AnimatePresence>
        {isResponding && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="mt-4 bg-zinc-50 rounded-xl p-4 overflow-hidden"
          >
            <label className="text-sm font-medium text-primary">Your Response</label>
            <textarea
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              className="w-full mt-2 border border-zinc-200 rounded-lg px-3 py-2 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              rows={3}
              placeholder="Write your response to this review..."
            />
            <div className="flex gap-2 mt-3">
              <motion.button
                onClick={onCancelRespond}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-4 py-2 text-sm border border-zinc-200 text-primary rounded-lg hover:bg-zinc-100 transition-colors"
              >
                Cancel
              </motion.button>
              <motion.button
                onClick={handleSubmit}
                disabled={isSubmitting || !responseText.trim()}
                whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                className="px-4 py-2 text-sm bg-primary hover:bg-ink-soft text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Response'}
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Actions */}
      {!review.responded && !isResponding && (
        <div className="mt-4 flex items-center gap-4">
          <motion.button
            onClick={onStartRespond}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="text-sm font-medium text-primary underline-offset-4 hover:underline transition-colors"
          >
            Respond to Review
          </motion.button>
          {review.reviewUrl && (
            <a
              href={review.reviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-muted hover:text-primary transition-colors"
            >
              View Original
            </a>
          )}
        </div>
      )}
    </motion.div>
  );
};

// Request Row Component
interface RequestRowProps {
  request: ReviewRequest;
  index: number;
  skipAnimation?: boolean;
}

const RequestRow: React.FC<RequestRowProps> = ({ request, index, skipAnimation }) => {
  const status = requestStatusConfig[request.status] || requestStatusConfig.pending;

  return (
    <motion.div
      initial={skipAnimation ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={skipAnimation ? { duration: 0 } : { delay: index * 0.05, duration: 0.2 }}
      className="p-4 hover:bg-zinc-100 transition-colors"
    >
      <div className="flex items-center gap-4">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-zinc-100 ring-1 ring-zinc-900/5">
          <span className="text-sm font-semibold text-zinc-700">
            {request.customerName.charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-primary">{request.customerName}</h3>
          <div className="flex items-center gap-3 mt-1 text-sm text-muted">
            {request.customerEmail && <span>{request.customerEmail}</span>}
            {request.customerPhone && <span>{request.customerPhone}</span>}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <motion.div
            key={request.status}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            <Tag tone={status.tone}>{status.label}</Tag>
          </motion.div>
          <span className="text-sm text-muted">{formatDate(request.createdAt)}</span>
        </div>
      </div>
    </motion.div>
  );
};

// Request Review Drawer
interface RequestReviewDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  websiteId: string;
  websiteName: string;
}

const RequestReviewDrawer: React.FC<RequestReviewDrawerProps> = ({ isOpen, onClose, websiteId, websiteName }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
  });

  const createRequestMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await axios.post('/api/reviews/requests', {
        ...data,
        websiteId,
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviewRequests'] });
      setFormData({ customerName: '', customerEmail: '', customerPhone: '' });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.customerName.trim()) return;
    createRequestMutation.mutate(formData);
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Request Review"
      subtitle={`Send a review request for ${websiteName}`}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-primary mb-1">Customer Name *</label>
          <input
            type="text"
            value={formData.customerName}
            onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
            className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-primary focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary/30"
            placeholder="Customer's name"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-primary mb-1">Email</label>
          <input
            type="email"
            value={formData.customerEmail}
            onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
            className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-primary focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary/30"
            placeholder="customer@email.com"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-primary mb-1">Phone</label>
          <input
            type="tel"
            value={formData.customerPhone}
            onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
            className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-primary focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary/30"
            placeholder="(555) 123-4567"
          />
        </div>
        <p className="text-sm text-muted bg-zinc-50 p-3 rounded-lg">
          A review request will be sent to the customer via their provided contact information.
        </p>
        <div className="flex gap-3 pt-4">
          <motion.button
            type="button"
            onClick={onClose}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 px-4 py-2.5 border border-zinc-200 text-primary rounded-lg hover:bg-zinc-100 transition-colors font-medium"
          >
            Cancel
          </motion.button>
          <motion.button
            type="submit"
            disabled={createRequestMutation.isPending}
            whileHover={{ scale: createRequestMutation.isPending ? 1 : 1.02 }}
            whileTap={{ scale: createRequestMutation.isPending ? 1 : 0.98 }}
            className="flex-1 px-4 py-2.5 bg-primary hover:bg-ink-soft text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
          >
            {createRequestMutation.isPending ? 'Sending...' : 'Send Request'}
          </motion.button>
        </div>
      </form>
    </Drawer>
  );
};

// Filter Dropdown Component - Google Style
interface FilterDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find(opt => opt.value === value) || options[0];
  const isFiltered = value !== '';

  return (
    <div className="relative">
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full border transition-all
          ${isFiltered
            ? 'bg-accent/10 border-accent/30 text-accent'
            : 'bg-white border-zinc-200 text-primary hover:bg-zinc-100'
          }
        `}
      >
        {selectedOption?.label}
        <motion.svg
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </motion.svg>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              variants={dropdownVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="absolute top-full left-0 mt-1 bg-white border border-zinc-200 rounded-xl shadow-lg z-20 min-w-[160px] overflow-hidden"
            >
              {options.map((option, index) => (
                <motion.button
                  key={option.value}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors
                    ${option.value === value
                      ? 'bg-accent/10 text-accent font-medium'
                      : 'text-primary hover:bg-zinc-100'
                    }
                  `}
                >
                  {option.label}
                </motion.button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReviewsPage;
